import {
    CommandCenterHousehold,
    OnboardingAccountsSummary,
    OnboardingStage,
    OnboardingStatus,
} from '@ndvr/customer-api';

import { parseServerDateTime } from 'utils/date-formatter';
import { formatCurrencyHuman } from 'utils/number-formatter';

export type OnboardingRow = OnboardingStatus & {
    household: CommandCenterHousehold;
    /** Missing while the summaries load or when they failed. */
    accounts?: OnboardingAccountsSummary;
};

/** The statuses whose household is listed, each with its accounts summary when there is one. */
export const toOnboardingRows = (
    statuses: OnboardingStatus[],
    households: CommandCenterHousehold[],
    summaries: OnboardingAccountsSummary[],
): OnboardingRow[] => {
    const householdsByGUID = new Map(households.map((household) => [household.globalAccountGUID, household]));
    const summariesByGUID = new Map(summaries.map((summary) => [summary.globalAccountGUID, summary]));
    return statuses.flatMap((status) => {
        const household = householdsByGUID.get(status.globalAccountGUID);
        return household ? [{ ...status, household, accounts: summariesByGUID.get(status.globalAccountGUID) }] : [];
    });
};

export const formatAssets = (amount: number | undefined) =>
    amount === undefined ? '—' : formatCurrencyHuman(amount, 'USD');

export const onboardingStages: Record<OnboardingStage, { label: string; badgeClass: string; complete?: boolean }> = {
    [OnboardingStage.NOT_INVITED]: {
        label: 'Not Invited',
        badgeClass: 'border-dashed border-[#8c8180] bg-white text-[#635958]',
    },
    [OnboardingStage.INVITED]: { label: 'Invited', badgeClass: 'border-transparent bg-[#eeece9]' },
    [OnboardingStage.CLIENT_IN_PROGRESS]: {
        label: 'Client in Progress',
        badgeClass: 'border-transparent bg-[#f4e6d9]',
    },
    [OnboardingStage.CLARO_REVIEW]: { label: 'Claro Review', badgeClass: 'border-transparent bg-[#d5edf0]' },
    [OnboardingStage.APPLICATION_OUTSTANDING]: {
        label: 'Application Outstanding',
        badgeClass: 'border-transparent bg-[#e7c9ac]',
    },
    [OnboardingStage.APPLICATION_SIGNED]: {
        label: 'Application Signed',
        badgeClass: 'border-transparent bg-[#ccecef]',
    },
    [OnboardingStage.ACCOUNTS_OPENED]: { label: 'Accounts Opened', badgeClass: 'border-[#2d2524] bg-white' },
    [OnboardingStage.ASSETS_RECEIVED]: {
        label: 'Assets Received',
        badgeClass: 'border-transparent bg-[#2d2524] text-white',
        complete: true,
    },
};

export const getStageEnteredTime = (status: OnboardingStatus): number | undefined => {
    if (!status.stageEnteredAt) {
        return undefined;
    }
    const time = parseServerDateTime(status.stageEnteredAt).getTime();
    return Number.isFinite(time) ? time : undefined;
};

export const getStageAgeLabel = (status: OnboardingStatus): string => {
    const entered = getStageEnteredTime(status);
    if (entered === undefined) {
        return 'Unknown';
    }
    const days = Math.floor(Math.max(0, Date.now() - entered) / 86_400_000);
    return days === 0 ? 'Less than a day' : `${days} ${days === 1 ? 'day' : 'days'}`;
};
